/**
 * Simplification de texte sur la page courante (option accessibilité).
 * Préserve la structure DOM : seuls les nœuds texte sont remplacés.
 */

import i18n from 'i18next';
import { readA11ySettingsFromStorage } from '@/lib/lyrra-a11y-settings';

const SIMPLIFY_ENDPOINT = '/api/a11y/simplify-text/batch';
const MIN_SEGMENT_CHARS = 8;
const BATCH_SIZE = 8;
const MANAGED_BY_EDUFLOW_ATTR = 'data-lyrra-simplified-text-managed';

const SKIP_ANCESTOR_SELECTORS = [
  'script',
  'style',
  'noscript',
  'svg',
  'canvas',
  '[aria-hidden="true"]',
  '[data-lyrra-magnifier-overlay]',
  '[data-lyrra-simplified-skip]',
  '.sr-only',
  '#lyrra-access-pad-indicator',
  '[role="dialog"]',
  '[data-a11y-panel]',
].join(',');

type TextTarget = {
  key: string;
  textNodes: Text[];
  originalJoined: string;
};

type StoredEntry = {
  textNodes: Text[];
  originals: string[];
};

const restoreRegistry = new Map<string, StoredEntry>();
const simplifyCache = new Map<string, string>();

let applyGeneration = 0;
const APPLIED_ATTR = 'data-lyrra-simplified-applied';

function cacheKey(lang: string, text: string): string {
  return `${lang}::${text}`;
}

export function isSimplifiedTextManagedByEduflowPlayer(): boolean {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector(`[${MANAGED_BY_EDUFLOW_ATTR}="eduflow"]`));
}

export function findSimplifiedTextRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return (
    document.getElementById('main-content') ??
    document.querySelector('main') ??
    document.getElementById('root') ??
    document.body
  );
}

function shouldSkipTextNode(node: Text): boolean {
  const parent = node.parentElement;
  if (!parent) return true;
  if (parent.closest(SKIP_ANCESTOR_SELECTORS)) return true;
  if (parent.isContentEditable) return true;
  const tag = parent.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return false;
}

function collectTextTargets(root: HTMLElement): TextTarget[] {
  const byParent = new Map<HTMLElement, Text[]>();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (shouldSkipTextNode(node)) continue;
    const trimmed = (node.nodeValue ?? '').trim();
    if (trimmed.length < 2) continue;
    const parent = node.parentElement;
    if (!parent) continue;
    const list = byParent.get(parent) ?? [];
    list.push(node);
    byParent.set(parent, list);
  }

  const targets: TextTarget[] = [];
  for (const [parent, textNodes] of byParent) {
    const originalJoined = textNodes.map((n) => n.nodeValue ?? '').join('');
    if (originalJoined.trim().length < MIN_SEGMENT_CHARS) continue;
    const key =
      parent.getAttribute('data-lyrra-simplify-key') ??
      `el-${targets.length}-${parent.tagName}-${originalJoined.slice(0, 24)}`;
    if (!parent.hasAttribute('data-lyrra-simplify-key')) {
      parent.setAttribute('data-lyrra-simplify-key', key);
    }
    targets.push({ key, textNodes, originalJoined });
  }
  return targets;
}

function storeOriginals(target: TextTarget): void {
  restoreRegistry.set(target.key, {
    textNodes: target.textNodes,
    originals: target.textNodes.map((n) => n.nodeValue ?? ''),
  });
}

function applySimplifiedToTarget(target: TextTarget, simplified: string): void {
  const nodes = target.textNodes;
  if (!nodes.length) return;
  const first = nodes[0]!;
  const last = nodes[nodes.length - 1]!;
  const leading = (first.nodeValue ?? '').match(/^\s*/)?.[0] ?? '';
  const trailing = (last.nodeValue ?? '').match(/\s*$/)?.[0] ?? '';
  first.nodeValue = `${leading}${simplified.trim()}${trailing}`;
  for (let i = 1; i < nodes.length; i++) {
    nodes[i]!.nodeValue = '';
  }
}

function restoreAll(): void {
  for (const entry of restoreRegistry.values()) {
    entry.textNodes.forEach((node, i) => {
      if (entry.originals[i] !== undefined) node.nodeValue = entry.originals[i]!;
    });
  }
  restoreRegistry.clear();
}

async function fetchSimplifiedBatch(
  segments: string[],
  language: string,
): Promise<string[]> {
  const res = await fetch(SIMPLIFY_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ segments, language, mode: 'falc' }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = (await res.json()) as { error?: { message?: string }; message?: string };
      detail = j?.error?.message ?? j?.message ?? '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `Simplify HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data?: { segments?: string[] };
    segments?: string[];
  };
  const out = json.data?.segments ?? json.segments;
  if (!Array.isArray(out) || out.length !== segments.length) {
    throw new Error('Réponse de simplification invalide');
  }
  return out;
}

/**
 * Simplifie ou restaure le contenu textuel de la page courante.
 * Retourne false si annulé / erreur / page gérée par le player EduFlow.
 */
export function isGlobalSimplifiedTextApplied(): boolean {
  const root = findSimplifiedTextRoot();
  return root?.getAttribute(APPLIED_ATTR) === 'true';
}

export async function applyGlobalSimplifiedTextToPage(enabled: boolean): Promise<boolean> {
  if (isSimplifiedTextManagedByEduflowPlayer()) {
    return true;
  }

  const gen = ++applyGeneration;
  const root = findSimplifiedTextRoot();

  if (!enabled) {
    restoreAll();
    root?.removeAttribute(APPLIED_ATTR);
    return true;
  }

  if (!root) return false;

  if (root.getAttribute(APPLIED_ATTR) === 'true') {
    return true;
  }

  const lang = (i18n.resolvedLanguage ?? i18n.language ?? document.documentElement.lang ?? 'fr')
    .split('-')[0]
    .toLowerCase();

  const targets = collectTextTargets(root);
  if (!targets.length) return true;

  for (const t of targets) storeOriginals(t);

  const pending: Array<{ target: TextTarget; text: string }> = [];
  for (const target of targets) {
    const ck = cacheKey(lang, target.originalJoined.trim());
    const cached = simplifyCache.get(ck);
    if (cached) {
      if (applyGeneration !== gen) return false;
      applySimplifiedToTarget(target, cached);
    } else {
      pending.push({ target, text: target.originalJoined.trim() });
    }
  }

  for (let i = 0; i < pending.length; i += BATCH_SIZE) {
    if (applyGeneration !== gen) return false;
    const chunk = pending.slice(i, i + BATCH_SIZE);
    try {
      const simplified = await fetchSimplifiedBatch(
        chunk.map((c) => c.text),
        lang,
      );
      if (applyGeneration !== gen) return false;
      chunk.forEach((item, idx) => {
        const result = simplified[idx] ?? item.text;
        simplifyCache.set(cacheKey(lang, item.text), result);
        applySimplifiedToTarget(item.target, result);
      });
    } catch (e) {
      restoreAll();
      throw e;
    }
  }

  root.setAttribute(APPLIED_ATTR, 'true');
  return true;
}

export function readSimplifiedTextModeEnabled(): boolean {
  return readA11ySettingsFromStorage().simplifiedTextMode;
}

/**
 * Simplifie uniquement les segments pas encore traités (SPA, changement d’onglet ?tab=, etc.).
 */
export async function applyGlobalSimplifiedTextIncremental(): Promise<void> {
  if (isSimplifiedTextManagedByEduflowPlayer()) return;
  const root = findSimplifiedTextRoot();
  if (!root || root.getAttribute(APPLIED_ATTR) !== 'true') return;

  const lang = (i18n.resolvedLanguage ?? i18n.language ?? document.documentElement.lang ?? 'fr')
    .split('-')[0]
    .toLowerCase();

  const targets = collectTextTargets(root).filter((t) => !restoreRegistry.has(t.key));
  if (!targets.length) return;

  for (const t of targets) storeOriginals(t);

  const pending: Array<{ target: TextTarget; text: string }> = [];
  for (const target of targets) {
    const ck = cacheKey(lang, target.originalJoined.trim());
    const cached = simplifyCache.get(ck);
    if (cached) {
      applySimplifiedToTarget(target, cached);
    } else {
      pending.push({ target, text: target.originalJoined.trim() });
    }
  }

  for (let i = 0; i < pending.length; i += BATCH_SIZE) {
    const chunk = pending.slice(i, i + BATCH_SIZE);
    const simplified = await fetchSimplifiedBatch(
      chunk.map((c) => c.text),
      lang,
    );
    chunk.forEach((item, idx) => {
      const result = simplified[idx] ?? item.text;
      simplifyCache.set(cacheKey(lang, item.text), result);
      applySimplifiedToTarget(item.target, result);
    });
  }
}
